/* eslint-disable react-native/no-color-literals */
// eslint-disable-next-line import/no-named-as-default
import t from "@constants/localization";
import { colors } from "@constants/theme";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import { getTodaysCrateDeliveries } from "@services/api";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Text,
  View,
} from "react-native";
import ListEmptyComponent from "../components/ListEmptyComponent";
import DeliveryCard from "./DeliveryCard";
import NoDeliveryCard from "./NoDeliveryCard";
import ReturnCard from "./ReturnCard";
import DeliveryCodeModal from "./DeliveryCodeModal";
import InfoTooltipModal from "../components/InfoTooltipModal";
import PaymentCard from "./PaymentCard";
import { useWebSocketEvents } from "../../../hooks/useWebSocketEvents";

const PATH = "screens/MyDeliveries/DeliveryReturn/OverView";

function ReturnProductInfoCardAndPaymentCard({ ledger, deliveryState }) {
  return deliveryState !== "Vacationing" ? (
    <PaymentCard ledger={ledger} showViewDetailsBtn={true} />
  ) : null;
}

const SectionHeader = () => {
  return (
    <View style={styles.sectionHeaderContainer}>
      <View style={styles.dashedLine} />
      <View style={styles.sectionHeaderTextContainer}>
        <Text style={styles.sectionHeaderText}>
          {t(`${PATH}/sectionHeader`)}
        </Text>
      </View>
      <View style={styles.dashedLine} />
    </View>
  );
};

function Overview() {
  const isFocused = useIsFocused();
  const [deliveriesData, setDeliveriesData] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deliveryState, setDeliveryState] = useState();
  const [visible, setVisible] = useState(false);
  const [infoTooltipVisible, setInfoTooltipVisible] = useState(false);
  const [infoTooltipData, setInfoTooltipData] = useState({});
  const [spokeDeliveryReturnDetails, setSpokeDeliveryReturnDetails] =
    useState(null);

  const { lastMessage } = useWebSocketEvents({});

  useEffect(() => {
    if (lastMessage !== null) {
      try {
        const event = JSON.parse(lastMessage.data);
        if (event.type === "REFRESH_OVERVIEW_PAGE") {
          fetchData();
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
      }
    }
  }, [lastMessage]);

  const fetchData = async () => {
    try {
      const { data = {} } = await getTodaysCrateDeliveries();
      if (data?.state === "Vacationing") {
        setDeliveryState(data?.state);
        setDeliveriesData(data);
        setIsLoading(false);
        return;
      }
      setDeliveriesData(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
    setIsLoading(false);
    setIsRefreshing(false);
  };

  useFocusEffect(
    useCallback(() => {
      if (isFocused) {
        fetchData();
      }
    }, [isFocused]),
  );

  const onRefresh = () => {
    setIsRefreshing(true);
    fetchData();
  };

  const sortUpdates = useMemo(
    () =>
      function () {
        if (deliveryState === "Vacationing") {
          return [];
        }
        const result = [];
        deliveriesData?.spokeDetails?.forEach((deliveryData) => {
          if (result.length === 0) {
            result.push(deliveryData);
          } else {
            result.push({
              type: "sectionHeader",
            });
            result.push(deliveryData);
          }
        });
        return result;
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliveriesData],
  );

  const renderItem = useCallback(
    ({ item }) => {
      if (item?.type === "sectionHeader") {
        return (
          <View key={item.type}>
            <SectionHeader />
          </View>
        );
      } else {
        return (
          <>
            {item?.deliveryDetails ? (
              <DeliveryCard
                deliveryDetails={item?.deliveryDetails}
                deliveryBoyInfo={item?.deliveryDetails?.deliveryBoyInfo}
                returnDetails={item.returnDetails}
                spokeName={item?.spokeName}
                deliveriesData={deliveriesData}
                setModalVisible={setVisible}
                setInfoTooltipVisible={setInfoTooltipVisible}
                setInfoTooltipData={setInfoTooltipData}
                setSpokeDeliveryReturnDetails={setSpokeDeliveryReturnDetails}
              />
            ) : (
              <NoDeliveryCard />
            )}
            {item.returnDetails ? (
              <ReturnCard
                returnDetails={item?.returnDetails}
                deliveryBoyInfo={item?.returnDetails?.deliveryBoyInfo}
                date={item?.deliveryDetails?.date}
                spokeName={item?.spokeName}
                setInfoTooltipVisible={setInfoTooltipVisible}
                setInfoTooltipData={setInfoTooltipData}
                setModalVisible={setVisible}
                deliveriesData={deliveriesData}
                setSpokeDeliveryReturnDetails={setSpokeDeliveryReturnDetails}
              />
            ) : (
              <></>
            )}
          </>
        );
      }
    },
    [deliveriesData],
  );

  const keyExtractor = (item, index) => `${item?.date}-${index}`;

  if (isLoading) {
    return (
      <View style={styles.loadingView}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <InfoTooltipModal
        visible={infoTooltipVisible}
        setVisible={setInfoTooltipVisible}
        data={infoTooltipData}
      />
      <FlatList
        data={sortUpdates()}
        renderItem={renderItem}
        ListHeaderComponent={
          <ReturnProductInfoCardAndPaymentCard
            ledger={deliveriesData?.ledger}
            data={deliveriesData?.spokeDetails}
            deliveryState={deliveryState}
          />
        }
        ListEmptyComponent={
          <ListEmptyComponent
            deliveryState={deliveryState}
            ledger={deliveriesData?.ledger}
          />
        }
        keyExtractor={keyExtractor}
        contentContainerStyle={styles.listContainer}
        onRefresh={onRefresh}
        refreshing={isRefreshing}
      />
      <DeliveryCodeModal
        visible={visible}
        setVisible={setVisible}
        spokeDeliveryReturnDetails={spokeDeliveryReturnDetails}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  listContainer: {
    flexGrow: 1,
    paddingBottom: 16,
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  sectionHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  dashedLine: {
    flex: 1,
    opacity: 0.7,
    height: 1,
    borderWidth: 0.5,
    borderColor: colors.divider,
    borderStyle: "dashed",
    borderRadius: 1,
  },
  sectionHeaderTextContainer: {
    backgroundColor: "#EEEEEE",
    alignSelf: "center",
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 40,
  },
  sectionHeaderText: {
    fontSize: 11,
    lineHeight: 15,
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.mediumBlack,
  },
});

export default Overview;
